import React from "react";
import {
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import { COLORS } from "../utils/colors";

type CursorPoint = {
  x: number;
  y: number;
  atFrame: number;
  click?: boolean;
};

type AnimatedCursorProps = {
  path: CursorPoint[];
  size?: number;
};

export const AnimatedCursor: React.FC<AnimatedCursorProps> = ({
  path,
  size = 48,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  if (path.length === 0) return null;

  let x = path[0].x;
  let y = path[0].y;
  let isClicking = false;
  let clickFrame = -999;

  for (let i = 0; i < path.length; i++) {
    const point = path[i];
    const nextPoint = path[i + 1];

    if (!nextPoint) {
      if (frame >= point.atFrame) {
        x = point.x;
        y = point.y;
        if (point.click && frame >= point.atFrame && frame < point.atFrame + 15) {
          isClicking = true;
          clickFrame = point.atFrame;
        }
      }
      break;
    }

    if (frame >= point.atFrame && frame < nextPoint.atFrame) {
      const segmentProgress = interpolate(
        frame,
        [point.atFrame, nextPoint.atFrame],
        [0, 1],
        {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
          easing: Easing.inOut(Easing.quad),
        }
      );

      x = interpolate(segmentProgress, [0, 1], [point.x, nextPoint.x]);
      y = interpolate(segmentProgress, [0, 1], [point.y, nextPoint.y]);

      if (point.click && frame >= point.atFrame && frame < point.atFrame + 15) {
        isClicking = true;
        clickFrame = point.atFrame;
      }
      break;
    }

    if (frame >= nextPoint.atFrame) {
      x = nextPoint.x;
      y = nextPoint.y;
      if (nextPoint.click && frame >= nextPoint.atFrame && frame < nextPoint.atFrame + 15) {
        isClicking = true;
        clickFrame = nextPoint.atFrame;
      }
    }
  }

  const entranceOpacity = interpolate(frame, [path[0].atFrame - 5, path[0].atFrame], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const rippleProgress = isClicking
    ? spring({
        frame,
        fps,
        delay: clickFrame,
        config: { damping: 15, stiffness: 150 },
        durationInFrames: 15,
      })
    : 0;

  const rippleScale = interpolate(rippleProgress, [0, 1], [0.3, 2.5]);
  const rippleOpacity = interpolate(rippleProgress, [0, 0.3, 1], [0, 0.6, 0]);

  const clickScale = isClicking
    ? interpolate(
        frame - clickFrame,
        [0, 3, 6, 10],
        [1, 0.8, 1.05, 1],
        { extrapolateRight: "clamp" }
      )
    : 1;

  // SVG cursor (no external image needed)
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 100,
      }}
    >
      {isClicking && (
        <div
          style={{
            position: "absolute",
            left: x - 25,
            top: y - 25,
            width: 50,
            height: 50,
            borderRadius: "50%",
            border: `3px solid ${COLORS.accent}`,
            transform: `scale(${rippleScale})`,
            opacity: rippleOpacity,
          }}
        />
      )}

      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        style={{
          position: "absolute",
          left: x,
          top: y,
          transform: `scale(${clickScale})`,
          transformOrigin: "2px 2px",
          opacity: entranceOpacity,
          filter: "drop-shadow(2px 4px 6px rgba(0,0,0,0.4))",
        }}
      >
        <path
          d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.45 0 .67-.54.35-.85L5.85 2.36a.5.5 0 0 0-.35.85z"
          fill="#FFFFFF"
          stroke="#000000"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  );
};
